"use client";

import React, { useState, useEffect } from "react";
import { Search, Menu, X, House, CirclePlay, Send, User, ChevronDown, Newspaper, Shield, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from '@/integrations/wordpress/WvcLogo';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
const decodeLabel = (label: string) => label.replace(/&amp;/g, "&");
const getCategorySlug = (label: string) => {
  return label.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w\-]+/g, '');
};
function HeaderMenuConsumer({
  mobile,
  onClose
}: {
  mobile?: boolean;
  onClose?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [currentPath, setCurrentPath] = useState("");
  const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null);
  useEffect(() => {
    setCurrentPath(window.location.pathname);
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setOpenSubmenuId(null);
      }
    };
    window.addEventListener("resize", handleResize, {
      passive: true
    });
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  if (loading) {
    return <div className={mobile ? "space-y-4" : "flex gap-6"}>
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className={mobile ? "h-6 w-full animate-pulse rounded bg-muted/50" : "h-4 w-16 animate-pulse rounded bg-muted/50"} />)}
      </div>;
  }
  const topLevelOrder = ["Berita", "Klarifikasi", "Kerjasama", "Zona PP", "Opini", "Tentang Kami", "Kontak Kami"];
  const processedItems = menuItems.filter(item => {
    const label = decodeLabel(item.label || "").toLowerCase().trim();
    const forbiddenKeywords = ["search", "cari", "kirim berita", "beranda"];
    const isForbidden = forbiddenKeywords.some(keyword => label.includes(keyword));
    const isOpiniWithoutHref = label === "opini" && !item.href;
    return !isForbidden && !isOpiniWithoutHref;
  }).sort((a, b) => {
    const indexA = topLevelOrder.indexOf(decodeLabel(a.label));
    const indexB = topLevelOrder.indexOf(decodeLabel(b.label));
    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  }).map(item => {
    const label = decodeLabel(item.label);
    const isBerita = label.toLowerCase() === "berita";
    const isCategoryItem = isBerita || item.href && item.href.includes('#/berita/');
    let finalHref = item.href || "#";
    if (isCategoryItem) {
      const slug = getCategorySlug(label);
      finalHref = `/categories/${slug}`;
    }
    const children = (item.children || []).map(child => {
      const childLabel = decodeLabel(child.label);
      const isChildCategory = child.href && child.href.includes('#/berita/');
      let childHref = child.href || "#";
      if (isChildCategory || isBerita) {
        const childSlug = getCategorySlug(childLabel);
        childHref = `/categories/${childSlug}`;
      }
      return {
        ...child,
        href: childHref
      };
    });
    return {
      ...item,
      href: finalHref,
      children
    };
  });
  const directItems = processedItems.slice(0, 6);
  const moreItems = processedItems.slice(6);
  if (mobile) {
    return <div className="flex flex-col gap-y-6">
        {processedItems.map((item, i) => {
        const label = decodeLabel(item.label);
        const isActive = currentPath === item.href || item.href !== "/" && currentPath.startsWith(item.href || "");
        const isExpanded = openSubmenuId === item.id;
        const children = item.children || [];
        const hasChildren = children.length > 0;
        return <div key={item.id} data-index={i} className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <Link to={item.href || "#"} onClick={onClose} className={`text-lg font-bold transition-colors py-2 ${isActive ? "text-white" : "text-white/90 hover:text-white"}`} tabIndex={0}>
                  {label}
                </Link>
                {hasChildren && <Button variant="ghost" size="icon" className="h-12 w-12 text-white hover:bg-white/10" onClick={() => setOpenSubmenuId(isExpanded ? null : item.id)} aria-label={isExpanded ? `Tutup submenu ${label}` : `Buka submenu ${label}`} aria-expanded={isExpanded}>
                    <ChevronDown className={`size-6 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`} />
                  </Button>}
              </div>
              
              {hasChildren && isExpanded && <div className="flex flex-col gap-4 pl-4 border-l-2 border-white/20 py-2 animate-in slide-in-from-top-2 duration-300">
                  {children.map((child, ci) => {
              const isChildActive = currentPath === child.href;
              return <Link key={child.id} data-index={ci} to={child.href || "#"} onClick={onClose} className={`text-base font-medium transition-colors py-1 ${isChildActive ? "text-white font-bold" : "text-white/70 hover:text-white"}`} tabIndex={0}>
                      {decodeLabel(child.label)}
                    </Link>;
            })}
                </div>}
            </div>;
      })}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex header-menu">
      <style dangerouslySetInnerHTML={{
      __html: `
        .header-menu li { position: relative; }
        .header-menu .submenu {
          display: block;
          position: absolute;
          background: #ffffff;
          z-index: 100;
          min-width: 220px;
          box-shadow: var(--shadow-lg);
          padding: 8px 0;
          border-radius: var(--radius-md);
          top: 100%;
          left: 0;
          border: 1px solid var(--border);
          opacity: 0;
          visibility: hidden;
          transform: translateY(10px);
          transition: opacity 0.25s ease-in-out, transform 0.25s ease-in-out, visibility 0.25s;
        }
        .header-menu li:hover > .submenu,
        .header-menu li:focus-within > .submenu {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }
        @media (min-width: 768px) and (max-width: 1024px) {
          .header-menu {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            overflow: hidden !important;
          }
          .header-menu .group a, 
          .header-menu .group div[role="button"],
          .header-menu .group .relative {
            font-size: 14px !important;
            padding-left: 6px !important;
            padding-right: 6px !important;
            white-space: nowrap !important;
          }
          .header-menu .group svg {
            margin-left: 3px !important;
          }
        }
        @media (min-width: 768px) {
          .header-menu li:nth-last-child(-n+2) .submenu {
            left: auto;
            right: 0;
          }
        }
        @media (min-width: 768px) and (max-width: 1024px) {
          .header-menu { display: none !important; }
          .flex.md\:hidden { display: flex !important; }
        }
        .header-menu .submenu li a {
          color: #000000;
          font-size: 14px;
          display: block;
          padding: 10px 20px;
          font-weight: 500;
          transition: background-color 0.2s, color 0.2s;
        }
        .header-menu .submenu li a:hover {
          color: var(--primary);
          background-color: var(--accent);
        }
      `
    }} />
      <NavigationMenuList className="flex-nowrap gap-x-6 lg:gap-x-8">
        {directItems.map((item, i) => {
        const label = decodeLabel(item.label);
        const isActive = currentPath === item.href || item.href !== "/" && currentPath.startsWith(item.href || "");
        const children = item.children || [];
        const hasChildren = children.length > 0;
        const isSubmenuOpen = openSubmenuId === item.id;
        return <NavigationMenuItem key={item.id} data-index={i} className="group">
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className={`group relative py-2 text-sm lg:text-lg font-semibold transition-all hover:text-white/80 hover:scale-105 flex items-center gap-1 ${isActive ? "text-white" : "text-white/90"}`} aria-haspopup={hasChildren ? "true" : undefined} aria-expanded={hasChildren ? isSubmenuOpen : undefined} tabIndex={0}>
                  {label}
                  {hasChildren && <ChevronDown className="size-3.5 opacity-70 group-hover:opacity-100 transition-opacity" />}
                  <span className={`absolute -bottom-1 left-0 h-[2.5px] bg-white transition-all duration-300 ${isActive ? "w-full" : "w-0 group-hover:w-full"}`} />
                </Link>
              </NavigationMenuLink>

              {hasChildren && <ul className="submenu">
                  {children.map((child, ci) => <li key={child.id} data-index={ci}>
                      <Link to={child.href || "#"} tabIndex={0}>
                        {decodeLabel(child.label)}
                      </Link>
                    </li>)}
                </ul>}
            </NavigationMenuItem>;
      })}

      {moreItems.length > 0 && <NavigationMenuItem className="group">
          <div className="group relative py-2 text-sm lg:text-lg font-semibold transition-all hover:text-white/80 hover:scale-105 flex items-center gap-1 text-white/90 cursor-pointer" tabIndex={0} aria-haspopup="true">
            Lainnya
            <ChevronDown className="size-3.5 opacity-70 group-hover:opacity-100 transition-opacity" />
            <span className="absolute -bottom-1 left-0 h-[2.5px] bg-white transition-all duration-300 w-0 group-hover:w-full" />
          </div>
          <ul className="submenu">
            {moreItems.map((item, i) => <li key={item.id} data-index={i}>
                <Link to={item.href || "#"} tabIndex={0}>
                  {decodeLabel(item.label)}
                </Link>
              </li>)}
          </ul>
        </NavigationMenuItem>}
      </NavigationMenuList>
    </NavigationMenu>;
}
function BottomMenu() {
  const [currentPath, setCurrentPath] = useState("");
  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, []);
  const menuItems = [{
    id: "home",
    label: "Home",
    href: "/"
  }, {
    id: "live",
    label: "Live",
    href: "/LivePage"
  }, {
    id: "zona-pp",
    label: "Zona PP",
    href: "/PPZonePage"
  }, {
    id: "kirim",
    label: "Kirim",
    href: "/SendNewsPage"
  }, {
    id: "pojok-ojol",
    label: "Pojok Ojol",
    href: "/RideHailingCornerPage"
  }];
  return <div className="relative z-70 mx-auto flex w-full max-w-7xl items-center justify-around px-1 h-full">
      {menuItems.map((item, i) => {
      const label = item.label;
      const isActive = currentPath === item.href;
      const lowerLabel = label.toLowerCase();
      const isZonaPP = lowerLabel === "zona pp";
      const isKirim = lowerLabel === "kirim";
      const isHome = lowerLabel === "home";
      return <Link key={item.id} data-index={i} to={item.href || "#"} className={`flex flex-col items-center justify-center transition-all duration-300 group flex-1 h-full py-0 ${isActive ? "text-white font-bold drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]" : "text-white/80 hover:text-white"}`}>
            {isZonaPP ? <div className="mb-1 flex items-center justify-center w-6 h-6">
                <img src="https://www.abadi.news/wp-content/uploads/2026/06/logo-pemuda-pancasila-2D-2026-3.png?v=hgj8ti2vxo" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/pemuda_pancasila.png" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/pemuda_pancasila.png" alt="Zona PP Icon" className="object-contain !h-[62px]" loading="lazy" />
              </div> : isKirim ? <div className={`bg-[#EA580C] p-1 rounded-lg shadow-md border border-white/20 mb-1 transition-all duration-300 group-hover:scale-110 w-9 h-9 flex items-center justify-center ${isActive ? "border-white" : ""}`}>
                <Send className="size-4 text-white" />
              </div> : <div className="flex flex-col items-center relative">
                {isHome && <House className="size-5 mb-1 text-white" />}
                {item.id === "pojok-ojol" && <MapPin className="size-5 mb-1 text-white" />}
                {lowerLabel === "live" && <>
                    <CirclePlay className="size-5 mb-1 text-white bg-primary" />
                    {isActive && <span className="absolute -top-1 -right-4 bg-red-600 text-white text-[8px] px-1 py-0.5 rounded-sm font-bold animate-pulse">
                        LIVE
                      </span>}
                  </>}
                {lowerLabel === "akun" && <User className="size-5 mb-1 text-white" />}
                {!["home", "pojok ojol", "live", "kirim", "akun", "zona pp"].includes(lowerLabel) && <Menu className="size-5 mb-1 text-white" />}
              </div>}
            <span className='uppercase text-center text-[10px] font-bold tracking-[0.3px] leading-tight text-white'>
              {label}
            </span>
          </Link>;
    })}
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <>
      <style dangerouslySetInnerHTML={{
      __html: `
        body { overflow-x: hidden !important; width: 100% !important; }
        #header-utama { min-height: 64px; height: 64px; }
        @media (min-width: 768px) { #header-utama { min-height: 68px; height: 68px; } }
        @media (min-width: 1024px) { #header-utama { min-height: 80px; height: 80px; } }
        
        .mobile-drawer-content { scroll-behavior: smooth; }

        @media (min-width: 768px) and (max-width: 1024px) {
          body { padding-bottom: 68px !important; }
          #header-utama { min-height: 68px; height: 68px; }
        }
        @media only screen (min-width: 768px) and (max-width: 1024px) {
          body { padding-bottom: 68px !important; }
          #header-utama { min-height: 68px; height: 68px; }
        }
      `
    }} />
      <header id="header-utama" className="fixed top-0 z-70 transition-all duration-300 flex items-center bg-[#F97316] text-white border-b border-white/10 !w-[1800px]">
        <div className="max-w-[1280px] px-4 flex items-center justify-between h-full !w-[415px] ![margin:0px_207px_0px_0px] md:px-8 lg:px-12">
          
          <div className="flex md:hidden items-center">
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-12 w-12" aria-label="Buka menu navigasi">
                  <Menu className="size-7" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[300px] bg-[#F97316] border-none p-0 flex flex-col text-white mobile-drawer-content">
                <SheetHeader className="p-6 flex flex-row items-center justify-between border-b border-white/10">
                  <SheetTitle className="sr-only">Menu Navigasi</SheetTitle>
                  <Link to="/" onClick={() => setIsMenuOpen(false)} className="flex items-center justify-center flex-1">
                    <WvcLogo className="h-8 w-auto" />
                  </Link>
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-12 w-12" onClick={() => setIsMenuOpen(false)} aria-label="Tutup menu navigasi">
                    <X className="size-7" />
                  </Button>
                </SheetHeader>
                
                <div className="flex-1 overflow-y-auto p-6">
                  <MenuProvider menu_id="1046">
                    <HeaderMenuConsumer mobile onClose={() => setIsMenuOpen(false)} />
                  </MenuProvider>
                </div>

                <div className="p-8 border-t border-white/10 flex flex-col items-center gap-4">
                  <WvcLogo className="h-8 w-auto" />
                  <p className="text-white text-sm font-bold text-center">Abadi.News - Menjaga Persatuan</p>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex-shrink-0 h-full flex items-center justify-center flex-1 md:justify-start md:flex-none">
            <Link to="/" className="flex items-center justify-center h-full min-h-[44px] !w-[300px]">
              <WvcLogo className="!h-[112px] !w-[500px] [&_svg]:!h-[112px] [&_svg]:!w-[500px]" />
            </Link>
          </div>

          <MenuProvider menu_id="1046" className="hidden md:flex h-full items-center">
            <HeaderMenuConsumer />
          </MenuProvider>

          <div className="flex items-center gap-2 h-full !w-[105px] md:gap-x-6 lg:gap-x-8">
            <div className="hidden items-center relative group h-full !w-[100px] sm:flex">
              <Input type="search" placeholder="Cari berita..." className="h-9 w-full flex min-w-0 border text-base shadow-xs outline-none focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive rounded-md border-transparent focus-visible:ring-white focus-visible:border-white transition-all bg-white text-black !w-[90px] !h-[50px] ![padding:4px_12px_4px_12px] file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:aria-invalid:ring-destructive/40" />
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground group-focus-within:text-[#F97316] transition-colors" />
            </div>

            <Button variant="ghost" size="icon" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive size-9 text-white !w-[24px] !h-[24px] disabled:pointer-events-none disabled:opacity-50 hover:text-accent-foreground dark:hover:bg-accent/50 md:hidden hover:bg-white/10" onClick={() => setIsSearchOpen(!isSearchOpen)} aria-label="Buka pencarian">
              <Search className="size-6" />
            </Button>

            <Button asChild className="h-9 justify-center gap-2 whitespace-nowrap text-sm [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive has-[>svg]:px-3 bg-[#C2410C] text-white font-extrabold min-w-[150px] rounded-lg shadow-md transition-all flex items-center !w-[100px] !h-[50px] ![padding:8px_10px_8px_10px] dark:aria-invalid:ring-destructive/40 disabled:pointer-events-none disabled:opacity-50 md:flex hover:bg-[#9A3412]">
              
            </Button>
          </div>
        </div>

        {isSearchOpen && <div className="absolute top-full left-0 w-full bg-[#F97316] p-4 border-t border-white/10 md:hidden animate-in slide-in-from-top duration-200">
            <div className="relative">
              <Input type="search" placeholder="Cari berita..." className="h-12 w-full pr-10 rounded-md border-transparent bg-white text-black" autoFocus />
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" />
            </div>
          </div>}
      </header>

      <nav className="fixed bottom-0 left-0 right-0 z-70 py-0 bg-[#F97316] border-t border-white/10 flex items-center transition-all">
        <div className="w-full h-full">
          <BottomMenu />
        </div>
      </nav>

      <div className="h-[64px] md:h-[68px] lg:h-[80px]" />
    </>;
}